'use client';

/**
 * Eén editor-sessie per productcustomisatie in deze browser-tab. Lazy: pas
 * aangemaakt bij de eerste betekenisvolle actie (zie de aanroepers in
 * design-editor-modal.tsx / file-uploader.tsx), niet bij het enkel openen
 * van een productpagina.
 */

type EditorSessionParams = {
  productId?: string;
  productSlug?: string;
  locale: string;
};

type CreateResult = { id: string; code: string; uploadCapability?: string };

/**
 * Welk product deze gecachte sessie hoort te beschrijven. Zonder deze sleutel
 * gaf de cache een treffer over een productwissel heen: klant configureert
 * product A, legt het in de winkelwagen en configureert daarna product B in
 * dezelfde tab (geen volledige herlaadbeurt), waarna B's autosave het
 * ontwerp van A overschreef en B's orderregel naar sessie A wees.
 *
 * Bewust de slug boven het id, en bewust ZONDER locale:
 * - file-uploader.tsx geeft id en slug mee, design-editor-modal.tsx alleen de
 *   slug; een samengestelde sleutel zou tussen die twee aanroepers mislopen en
 *   per product een tweede, overbodige sessie aanmaken.
 * - Een taalwissel is geen ander ontwerp. De sessie zou dan zonder reden
 *   opnieuw beginnen en het lopende ontwerp uit beeld raken; `locale` is op de
 *   sessie enkel beschrijvende informatie.
 */
type ProductRef = Pick<EditorSessionParams, 'productId' | 'productSlug'>;

function sessionKey(params: ProductRef): string {
  return params.productSlug || params.productId || '';
}

let pending: { key: string; promise: Promise<CreateResult | null> } | null = null;
let current: (CreateResult & { key: string }) | null = null;

export function getEditorSessionUploadCapability(sessionId?: string): string | undefined {
  return sessionId && current?.id === sessionId ? current.uploadCapability : undefined;
}

/**
 * Geef `product` mee zodra de aanroeper weet om welk product het gaat.
 *
 * Zonder die controle bleef de tweede helft van hetzelfde probleem staan: de
 * cache wordt nergens geleegd bij navigatie, dus na product A ontwerpen en
 * daarna product B in de winkelwagen leggen zonder daar te uploaden, hing de
 * code van sessie A alsnog aan de orderregel van B.
 */
export function getCurrentEditorSessionCode(product?: ProductRef): string | null {
  if (!current) return null;
  if (product && sessionKey(product) !== current.key) return null;
  return current.code;
}

/**
 * Zelfde controle als getCurrentEditorSessionCode hierboven, maar geeft het
 * database-id terug in plaats van de weergavecode. Voor aanroepers die de
 * sessie als foreign key willen meesturen (bijv. file-uploader.tsx naar
 * /api/upload) zonder eerst zelf van code naar id te hoeven vertalen.
 */
export function getCurrentEditorSessionId(product?: ProductRef): string | null {
  if (!current) return null;
  if (product && sessionKey(product) !== current.key) return null;
  return current.id;
}

export async function getOrCreateEditorSession(params: EditorSessionParams): Promise<string | null> {
  const key = sessionKey(params);
  if (current?.key === key) return current.code;
  if (pending?.key === key) return (await pending.promise)?.code ?? null;

  const promise: Promise<CreateResult | null> = fetch('/api/editor-sessions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  })
    .then((res) => (res.ok ? res.json() : null))
    .catch(() => null);

  pending = { key, promise };
  const result = await promise;

  // Alleen de laatst gestarte aanvraag mag `current` zetten. Wisselt de klant
  // van product terwijl de vorige aanmaak nog loopt, dan hoort de trage
  // eerdere sessie niet alsnog de actieve te worden.
  const isLatest = pending?.promise === promise;
  if (isLatest) pending = null;
  if (result?.code && isLatest) current = { key, id: result.id, code: result.code, uploadCapability: result.uploadCapability };
  return result?.code ?? null;
}

export function syncEditorSessionSnapshot(payload: {
  configuration?: unknown;
  designData?: unknown;
  previewUrl?: string;
}, sessionCode?: string) {
  // An earlier product's session creation can finish after navigation has
  // changed `current`. Autosave must sync to the code returned for its draft.
  const code = sessionCode ?? current?.code;
  if (!code) return;
  try {
    // Bewust GEEN keepalive. De Fetch-standaard begrenst een keepalive-body op
    // 64 KiB en laat hem daarboven stil mislukken, terwijl de sync-route zelf
    // tot 2 MB aanneemt. Deze functie wordt alleen aangeroepen vanuit de
    // autosave in design-editor-modal.tsx. Bij pagehide wordt het lokale
    // concept synchroon bewaard; de cloudkopie blijft best-effort.
    fetch(`/api/editor-sessions/${code}/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then((response) => {
      if (response.status === 403 && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('printen:editor-cloud-access-required', { detail: { code } }));
      }
    }).catch(() => { /* best-effort, net als de localStorage-autosave hiernaast */ });
  } catch {
    /* best-effort: JSON.stringify kan falen op circulaire referenties (Fabric.js canvassen) */
  }
}

export function recordEditorSessionEvent(eventType: string, metadata?: Record<string, unknown>) {
  const code = current?.code;
  if (!code) return;
  try {
    fetch(`/api/editor-sessions/${code}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventType, metadata }),
      keepalive: true,
    }).catch(() => { /* best-effort */ });
  } catch {
    /* best-effort: JSON.stringify kan falen op circulaire referenties */
  }
}

/** Voor tests: reset de module-state tussen scenario's. */
export function _resetEditorSessionForTests() {
  current = null;
  pending = null;
}
